/**
 * The workshop's own company logo (owner, 2026-11-03): shown on the printed work order and on every
 * document and e-mail the workshop gives its customers, never on the program's own top bar.
 *
 * The file lives in the private storage bucket "logos" under `<workshop_id>/logo-<version>.<ext>`;
 * workshops.settings.logo records which one is current. Only PNG, JPEG and WebP are accepted (no SVG:
 * an SVG can carry scripts). The app shrinks the image on the device to at most 600 x 300 px keeping the
 * aspect ratio, PNG and WebP become PNG (transparency stays, every e-mail program shows PNG), JPEG stays
 * JPEG, and the result must be at most 300 KB. The same limits are in the database migration
 * (20261214000000_workshop_logo.sql).
 */

export const LOGO_MAX_WIDTH = 600;
export const LOGO_MAX_HEIGHT = 300;
export const LOGO_MAX_BYTES = 300 * 1024;
/** A picked file larger than this is refused before it is even decoded. */
export const LOGO_MAX_INPUT_BYTES = 15 * 1024 * 1024;
export const LOGO_INPUT_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
export type LogoInputType = (typeof LOGO_INPUT_TYPES)[number];
export type LogoOutputType = "image/png" | "image/jpeg";

/** settings.logo of a workshop. */
export interface WorkshopLogo {
  /** Storage path in the "logos" bucket: `<workshop_id>/logo-<version>.<png|jpg>`. */
  path: string;
  /** Changes with every upload, so cached copies are never shown after a replacement. */
  version: string;
  width: number;
  height: number;
}

/** Size that fits into max width x max height keeping the aspect ratio; never enlarges. */
export function logoFit(width: number, height: number, maxWidth = LOGO_MAX_WIDTH, maxHeight = LOGO_MAX_HEIGHT): { width: number; height: number } {
  if (!(width > 0) || !(height > 0)) return { width: 0, height: 0 };
  const scale = Math.min(1, maxWidth / width, maxHeight / height);
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/**
 * What the file really is, from its first bytes (the browser's file type is only the name's ending):
 * PNG, JPEG or WebP, or null for anything else (SVG, GIF, HTML, PDF ...).
 */
export function sniffLogoType(bytes: Uint8Array): LogoInputType | null {
  const b = (i: number) => bytes[i] ?? -1;
  if (b(0) === 0x89 && b(1) === 0x50 && b(2) === 0x4e && b(3) === 0x47 && b(4) === 0x0d && b(5) === 0x0a && b(6) === 0x1a && b(7) === 0x0a) return "image/png";
  if (b(0) === 0xff && b(1) === 0xd8 && b(2) === 0xff) return "image/jpeg";
  const ascii = (from: number, to: number) => String.fromCharCode(...bytes.slice(from, to));
  if (bytes.length >= 12 && ascii(0, 4) === "RIFF" && ascii(8, 12) === "WEBP") return "image/webp";
  return null;
}

/**
 * Checks a picked file before it is decoded: the error code to show, or null when it may go on.
 * `bytes` are the first bytes of the file (at least 12).
 */
export function logoFileError(file: { type: string; size: number }, bytes: Uint8Array): "LOGO_TYPE" | "LOGO_TOO_LARGE" | null {
  const real = sniffLogoType(bytes);
  if (!real || !(LOGO_INPUT_TYPES as readonly string[]).includes(file.type || real)) return "LOGO_TYPE";
  if (file.size > LOGO_MAX_INPUT_BYTES) return "LOGO_TOO_LARGE";
  return null;
}

/** PNG and WebP become PNG (transparency kept), JPEG stays JPEG. */
export function logoOutputType(input: LogoInputType): LogoOutputType {
  return input === "image/jpeg" ? "image/jpeg" : "image/png";
}

/** A new version string: time based, lower case letters and digits only (used in the file name). */
export function newLogoVersion(now = Date.now()): string {
  return now.toString(36);
}

export function logoPath(workshopId: string, version: string, type: LogoOutputType): string {
  return `${workshopId}/logo-${version}.${type === "image/jpeg" ? "jpg" : "png"}`;
}

const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const PATH = new RegExp(`^(${UUID})/logo-([0-9a-z]{1,20})\\.(png|jpg)$`);

/** settings.logo as stored, checked: null when missing or not this workshop's logo. */
export function cleanLogo(raw: unknown, workshopId: string): WorkshopLogo | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const path = typeof r.path === "string" ? r.path : "";
  const m = PATH.exec(path);
  if (!m || m[1] !== workshopId) return null;
  const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.round(v) : 0);
  return { path, version: typeof r.version === "string" && r.version ? r.version : m[2]!, width: n(r.width), height: n(r.height) };
}

/**
 * Quality steps for shrinking under LOGO_MAX_BYTES: JPEG lowers its quality first, then (like PNG,
 * which has no quality) the size by 20% per step. The app tries them in order and stops at the first fit.
 */
export function logoAttempts(type: LogoOutputType, width: number, height: number): { width: number; height: number; quality?: number }[] {
  const out: { width: number; height: number; quality?: number }[] = [];
  const fit = logoFit(width, height);
  if (type === "image/jpeg") for (const quality of [0.92, 0.85, 0.75]) out.push({ ...fit, quality });
  else out.push(fit);
  let scale = 1;
  for (let i = 0; i < 5; i++) {
    scale *= 0.8;
    const w = Math.max(1, Math.round(fit.width * scale));
    const h = Math.max(1, Math.round(fit.height * scale));
    out.push(type === "image/jpeg" ? { width: w, height: h, quality: 0.75 } : { width: w, height: h });
  }
  return out;
}
